import { Head, Link } from '@inertiajs/react'
import { ReactNode } from 'react'

// The Chestnut brand mark. This is the same asset the page <head> already uses
// as the favicon; swap this one constant if a proper hosted logo file arrives.
export const BRAND_MARK_URL =
  'https://assets.cdn.filesafe.space/BWVM8i8KZhzdTN9n3nB6/media/6945b1c466d57849d1140616.png'

interface AuthLayoutProps {
  /** Browser tab title for the page. */
  title: string
  children: ReactNode
}

/**
 * The signed-out shell shared by Sign In, Forgot Password and Reset Password:
 * brand mark, wordmark, tagline, centered card and footer line. Every colour
 * and font comes from the Chestnut theme tokens in application.css so the three
 * screens can never drift apart again.
 */
export default function AuthLayout({ title, children }: AuthLayoutProps) {
  return (
    <div className="relative min-h-screen bg-chestnut-cream">
      <Head title={title} />

      {/* Soft forest wash behind the header — decorative only. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-chestnut-forest/10 to-transparent"
      />

      <div className="relative flex min-h-screen flex-col justify-center px-4 py-10 sm:px-6 sm:py-14">
        <div className="mx-auto w-full max-w-md">
          <header className="flex flex-col items-center text-center">
            <img
              src={BRAND_MARK_URL}
              alt=""
              aria-hidden="true"
              className="h-16 w-16 rounded-full bg-white object-contain p-2 shadow-sm ring-1 ring-chestnut-gold/50"
            />
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-chestnut-forest sm:text-4xl">
              Chestnut Capital
            </h1>
            <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-chestnut-charcoal/60">
              Building Generational Wealth With Proven Strategies
            </p>
          </header>

          <main className="mt-8">
            <div className="overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(26,60,52,0.10)] ring-1 ring-chestnut-gray">
              <div aria-hidden="true" className="h-1 w-full bg-chestnut-gold" />
              <div className="px-6 py-8 sm:px-8">{children}</div>
            </div>
          </main>

          <footer className="mt-8 text-center text-sm text-chestnut-charcoal/60">
            <p>Serving affluent families with integrity and transparency.</p>
            <p className="mt-1 text-xs">
              &copy; {new Date().getFullYear()} Chestnut Capital
            </p>
          </footer>
        </div>
      </div>
    </div>
  )
}

/** Heading + optional helper text, styled the same on all three auth cards. */
export function AuthCardHeading({
  title,
  description,
}: {
  title: string
  description?: ReactNode
}) {
  return (
    <div className="mb-6">
      <h2 className="font-heading text-2xl font-semibold text-chestnut-charcoal">
        {title}
      </h2>
      {description && (
        <p className="mt-2 text-sm leading-relaxed text-chestnut-charcoal/70">
          {description}
        </p>
      )}
    </div>
  )
}

/**
 * Form-level message banner. Errors are announced to screen readers as soon as
 * they appear; the success variant is polite so it never interrupts typing.
 */
export function AuthMessage({
  tone,
  children,
}: {
  tone: 'error' | 'success'
  children: ReactNode
}) {
  const isError = tone === 'error'
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={
        isError
          ? 'mb-6 rounded-lg border border-status-error/30 bg-status-error/10 px-4 py-3 text-sm text-status-error'
          : 'mb-6 rounded-lg border border-status-success/30 bg-status-success/10 px-4 py-3 text-sm text-status-success'
      }
    >
      {children}
    </div>
  )
}

/** The "Back to sign in" link every auth card ends with. */
export function BackToSignIn({ className = '' }: { className?: string }) {
  return (
    <div className={`mt-6 text-center ${className}`}>
      <Link href="/login" className="auth-link text-sm">
        Back to sign in
      </Link>
    </div>
  )
}
